import { FC, useState, DragEvent } from 'react';
import { Attachment } from '@/library/types/files';
import { upload } from '@/api';

import cameraIcon from '@/library/assets/svg/common/camera-line.svg';
import SvgRender from '@/library/components/general/SvgRender';

type GalleryReplaceImageButtonType = {
  group: string;
  onReplace: (files: Attachment[]) => void;
  setSelectedFile: (file: Attachment) => void;
  setGalleryFiles: (files: Attachment[]) => void;
};

const GalleryReplaceImageButton: FC<GalleryReplaceImageButtonType> = ({
  group,
  onReplace,
  setSelectedFile,
  setGalleryFiles
}) => {
  const uploadPath = '/files';
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const onUploadFiles = async (file: File): Promise<void> => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const data = new FormData();
      data.append('group', group);
      data.append('files[]', file, file.name);

      const res = await upload<Attachment[]>(uploadPath, data);
      const uploadedFiles = res.data;

      if (uploadedFiles.length) {
        setGalleryFiles(uploadedFiles);
        setSelectedFile(uploadedFiles[0]);
        onReplace(uploadedFiles);
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle file selection via click
  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await onUploadFiles(file);
  };

  // Handle drag & drop
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    await onUploadFiles(file);
  };

  return (
    <div
      className={`gallery__header--icon gallery__header--icon-rotate 
        ${isLoading ? 'opacity-5 pe-none' : ''} `}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={() => !isLoading && document.getElementById('gallery-upload-input')?.click()}
      style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
    >
      <SvgRender
        src={cameraIcon}
        style={{ width: 22, height: 22 }}
        className={`${isDragging ? 'text-yellow' : ''}`}
      />
      <input
        id="gallery-upload-input"
        type="file"
        onChange={onFileChange}
        disabled={isLoading}
        multiple={false}
        accept=".png, .jpg, .jpeg, .gif"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default GalleryReplaceImageButton;
